import { useCallback, useState } from "react";
import { createRoute, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ClubGuard } from "@/components/ClubGuard";
import { useOrganizerClubs } from "@/lib/clubs/queries";
import { useClubChannel } from "@/lib/clubs/useClubChannel";
import { useClubDetails } from "@/lib/clubs/gatherings";
import {
  isRateLimitedError,
  roomProgressKey,
  useNudgeMember,
  useRoomProgress,
} from "@/lib/clubs/rooms";
import { FacilitatorDashboard } from "@/components/rooms/FacilitatorDashboard";
import { clubsRoute } from "./clubs";

function FacilitatorRoute() {
  const params = useParams({ from: clubsRoomsDetailRoute.id });
  const { id: clubId, roomId } = params;
  const qc = useQueryClient();

  const { data: clubs = [] } = useOrganizerClubs();
  const club = clubs.find((c) => c.id === clubId);
  const { data: clubDetails } = useClubDetails(clubId);
  const room = clubDetails?.readingRooms.find((r) => r.id === roomId);

  const progressQuery = useRoomProgress(roomId);
  const nudgeMutation = useNudgeMember(roomId);

  const [nudgesSentToday, setNudgesSentToday] = useState(0);
  const [isCapped, setIsCapped] = useState(false);
  const [batchError, setBatchError] = useState<Error | null>(null);

  useClubChannel(clubId, (event) => {
    if (event.type === "nudge:received" && event.payload.roomId === roomId) {
      qc.invalidateQueries({ queryKey: roomProgressKey(roomId) });
    }
  });

  const handleSendNudges = useCallback(
    (receiverIds: string[], message: string, onSent: () => void) => {
      setBatchError(null);
      let sentInBatch = 0;
      let cappedDuring = false;

      const sendNext = (index: number) => {
        if (index >= receiverIds.length) {
          if (sentInBatch > 0) {
            setNudgesSentToday((n) => n + sentInBatch);
          }
          if (!cappedDuring) {
            onSent();
          }
          return;
        }
        const receiverId = receiverIds[index];
        nudgeMutation.mutate(
          { receiverId, message: message || undefined },
          {
            onSuccess: () => {
              sentInBatch += 1;
              sendNext(index + 1);
            },
            onError: (err) => {
              if (isRateLimitedError(err)) {
                cappedDuring = true;
                setIsCapped(true);
                if (sentInBatch > 0) {
                  setNudgesSentToday((n) => n + sentInBatch);
                  sentInBatch = 0;
                }
                setBatchError(
                  new Error("Daily nudge cap reached. Try again tomorrow."),
                );
              } else {
                setBatchError(err as Error);
                if (sentInBatch > 0) {
                  setNudgesSentToday((n) => n + sentInBatch);
                  sentInBatch = 0;
                }
              }
            },
          },
        );
      };

      sendNext(0);
    },
    [nudgeMutation],
  );

  if (progressQuery.isLoading && !progressQuery.data) {
    return (
      <div role="status" aria-live="polite" style={{ padding: 32 }}>
        Loading…
      </div>
    );
  }

  if (progressQuery.isError || !progressQuery.data) {
    return (
      <div role="alert" style={{ padding: 32 }}>
        Couldn't load reading-room progress. Try refreshing.
      </div>
    );
  }

  return (
    <FacilitatorDashboard
      clubName={club?.name ?? "Club"}
      progress={progressQuery.data}
      bookTitle={room?.book?.title ?? null}
      nudgesSentToday={nudgesSentToday}
      isCapped={isCapped}
      isNudging={nudgeMutation.isPending}
      nudgeError={batchError ?? nudgeMutation.error ?? null}
      onSendNudges={handleSendNudges}
    />
  );
}

export const clubsRoomsDetailRoute = createRoute({
  getParentRoute: () => clubsRoute,
  path: "/$id/rooms/$roomId",
  component: () => (
    <ClubGuard requireClubId>
      <FacilitatorRoute />
    </ClubGuard>
  ),
});
